import { makeAutoObservable, observable, runInAction, toJS } from 'mobx'
import { demo } from 'page/catalog/store/CatalogStore'
import { Item, Items } from 'type/Item'
import { SearchRequest } from 'type/Search'
import { skipAsync } from 'util/async'
import { fetchJson } from 'util/fetch'

type State = 'none' | 'busy' | 'ready'

type Cached = {
  page: number
  count: number
  items: Item[]
}

function getComboGender(item: Item) {
  const gender = item.dress_gender ?? ''
  if (['male', 'female'].includes(gender)) return gender
  return ''
}

function getComboShape(item: Item) {
  return item.dress_shape_type === 'bottom' ? 'top' : 'bottom'
}

function buildRequest(gender: string, shape: string): SearchRequest {
  const { group_id, sort_by, use_tsum_data } = toJS(demo.it.form.request)
  const filters: SearchRequest['filters'] = {
    dress_gender: gender ? [gender, 'unisex'] : undefined,
    dress_shape_type: [shape],
    combinations: [1],
  }
  return { group_id, sort_by, use_tsum_data, query: '', page: 1, limit: 100, filters }
}

async function loadItems(request: SearchRequest, page: number) {
  return await fetchJson<Items>('/api/stand/items', {
    method: 'POST',
    body: JSON.stringify({ ...request, page }),
  })
}

const cache = new Map<string, Cached>

export class ComboItemsStore {
  readonly request: SearchRequest

  state: State = 'ready'
  page = 1
  total = 0
  items: Item[] = []

  static clearCache(): void {
    cache.clear()
  }

  constructor(item: Item) {
    const gender = getComboGender(item)
    const shape = getComboShape(item)
    this.request = buildRequest(gender, shape)
    makeAutoObservable(this, { request: false, items: observable.ref })
    this.init(item)
  }

  more() {
    if (this.state !== 'ready') return
    if (this.total > this.items.length) this.loadSync(true)
  }

  private init(item: Item) {
    const cached = cache.get(JSON.stringify(this.request))
    const mix = demo.it.canMix(item)
    if (!mix) {
      this.items = []
    } else if (cached) {
      this.page = cached.page
      this.total = cached.count
      this.items = cached.items
    } else {
      this.items = []
      this.loadSync()
    }
  }

  private readonly loadSync = skipAsync(async (more?: boolean) => {
    runInAction(() => {
      this.state = 'busy'
    })
    try {
      const page = more ? this.page + 1 : 1
      const { count, items: it } = await loadItems(this.request, page)

      const items = more ? [...toJS(this.items), ...it] : it
      cache.set(JSON.stringify(this.request), { page, count, items })

      runInAction(() => {
        this.state = 'ready'
        this.page = page
        this.total = count
        this.items = items
      })
    } catch (e) {
      console.error('search failed', e)
      cache.delete(JSON.stringify(this.request))
      runInAction(() => {
        this.state = 'ready'
      })
    }
  })
}
